import { parseDateValue } from '@ben/common/date';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import MantineModal from '@ben/orion/mantine-modal';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  RenewalConfig,
  RenewalConfigFormErrors,
} from '@ben/shared/types/columbia';
import { FocusTrap } from '@mantine/core';
import { differenceInCalendarDays } from 'date-fns';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import RenewalWindowForm from './renewal-window-form';
import TimeframeWarningModal from './timeframe-warning-modal';

interface RenewalWindowModalProps {
  title: string;
  description?: string;
  buttonLabel: string;
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: RenewalConfig) => void;
  isSubmitting?: boolean;
  initialValues?: RenewalConfig;
}

const RenewalWindowModal = ({
  title,
  description,
  buttonLabel,
  opened,
  onClose,
  onSubmit,
  isSubmitting,
  initialValues,
}: RenewalWindowModalProps) => {
  const defaultFormValues: RenewalConfig = {
    scheduledRenewalDate: '',
    renewalWindowStartDate: undefined,
    renewalWindowEndDate: undefined,
    hasConfigWindow: false,
    hasOptInWindow: false,
    hasOptOutWindow: false,
  };

  const [formValues, setFormValues] = useState<RenewalConfig>(
    initialValues ?? defaultFormValues
  );
  const [daysUntilRenewal, setDaysUntilRenewal] = useState<number>();
  const [formErrors, setFormErrors] = useState<RenewalConfigFormErrors>({});

  const handleClose = () => {
    onClose();
    // clear up form state
    setFormValues(initialValues ?? defaultFormValues);
    setDaysUntilRenewal(undefined);
    setFormErrors({});
  };

  const onFormSubmit = () => {
    const formErrors: RenewalConfigFormErrors = {};

    if (!formValues.scheduledRenewalDate) {
      formErrors.scheduledRenewalDateError = true;
    }

    if (
      (formValues.hasOptInWindow ||
        formValues.hasOptOutWindow ||
        formValues.hasConfigWindow) &&
      (!formValues.renewalWindowStartDate || !formValues.renewalWindowEndDate)
    ) {
      formErrors.renewalWindowDateError = true;
    }

    setFormErrors(formErrors);
    if (!isEmpty(formErrors) || !formValues.scheduledRenewalDate) {
      return;
    }

    const daysFromToday = differenceInCalendarDays(
      parseDateValue(formValues.scheduledRenewalDate),
      new Date()
    );

    if (daysFromToday <= 7) {
      setDaysUntilRenewal(daysFromToday);
      return;
    }

    onSubmit(formValues);
  };

  if (daysUntilRenewal) {
    return (
      <TimeframeWarningModal
        daysUntilRenewal={daysUntilRenewal}
        onClose={() => {
          setDaysUntilRenewal(undefined);
        }}
        onConfirm={() => {
          setDaysUntilRenewal(undefined);
          onSubmit(formValues);
        }}
      />
    );
  }

  return (
    <MantineModal opened={opened} onClose={handleClose}>
      {/* to stop date picker calendar being opened on initial open */}
      <FocusTrap.InitialFocus />
      <Header>
        <Typography variant={TypographyVariant.h2}>{title}</Typography>
        {description && (
          <Typography variant={TypographyVariant.bodyLg1}>
            {description}
          </Typography>
        )}
      </Header>
      <Body>
        <RenewalWindowForm
          formErrors={formErrors}
          formValues={formValues}
          setFormValues={setFormValues}
        />
      </Body>
      <Footer>
        <Button
          label="Cancel"
          variant={ButtonVariant.secondary}
          onClick={handleClose}
          minWidth="208px"
        />
        <Button
          label={buttonLabel}
          isLoading={isSubmitting}
          onClick={onFormSubmit}
          minWidth="208px"
        />
      </Footer>
    </MantineModal>
  );
};

export default RenewalWindowModal;

const Header = styled(MantineModal.Header)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    text-align: center;
    align-items: center;
  `}
`;

const Body = styled(MantineModal.Body)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    padding: ${theme.spacing(0, 4)};
  `}
`;

const Footer = styled(MantineModal.Footer)`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(3)};
  `}
`;
